import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { api } from "../../../utils/api";
import { startAssistant, stopAssistant } from "../../../utils/vapiClient";
import { DEPLOYMENT_CONFIG } from "../../../config/deployment";

import DashboardHeader from "./widgets/DashboardHeader";
import CallControls from "./widgets/CallControls";
import CallStatusBanner from "./widgets/CallStatusBanner";
import FeedbackTable from "./sections/ReviewScores/FeedbackTable";
import AccountInfo from "./sections/Account/AccountInfo";
import LoadingSpinner from "./widgets/LoadingSpinner";
import AccountStrip from "./widgets/AccountStrip";
import TranscriptPlayer from "./widgets/TranscriptPlayer";

// Local helpers (kept in this file as requested)
function generateGUID() {
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}
const getFileName = (filePath) =>
	filePath ? filePath.split(/[\\/]/).pop() : "-";

const getRingtoneUrl = () => {
	const basePath = DEPLOYMENT_CONFIG.getCurrentPath();
	return process.env.REACT_APP_RINGTONE_URL ||
		`${basePath}/ringtone/professional-ringtone.mp3`;
};

const TraineeDashboard = ({ user }) => {
	const [dashboardData, setDashboardData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");

	const [feedback, setFeedback] = useState([]);
	const [feedbackLoading, setFeedbackLoading] = useState(false);

	const [callType, setCallType] = useState("Anytime Script");
	const [customerMood, setCustomerMood] = useState("Happy");
	const [gender, setGender] = useState("M");
	const [issue, setIssue] = useState("");
	const [voiceId, setVoiceId] = useState("Rohan");

	const [callState, setCallState] = useState({
		active: false,
		callId: null,
		starting: false,
		stopping: false,
	});

	const [feedbackId, setFeedbackId] = useState(generateGUID());

	const voiceList = [
		// { name: "Cole", age: 22, gender: "M" },
		// { name: "Harry", age: 24, gender: "M" },
		// { name: "Spencer", age: 26, gender: "F" },
		// { name: "Kylie", age: 23, gender: "F" },
		// { name: "Savannah", age: 25, gender: "F" },
		// { name: "Paige", age: 26, gender: "F" },
		// { name: "Rohan", age: 24, gender: "M" },
		// { name: "Elliot", age: 25, gender: "M" }
		{ name: "gYr8yTP0q4RkX1HnzQfX", age: 50, gender: "M", provider: "11labs" },
		{ name: "aTxZrSrp47xsP6Ot4Kgd", age: 50, gender: "F", provider: "11labs" },
	];

	const getRandomVoiceId = (gender) => {
		const filtered = voiceList.filter((v) => v.gender === gender);
		if (filtered.length === 0) return "Rohan";
		const idx = Math.floor(Math.random() * filtered.length);
		return filtered[idx];
	};

	// Call Vapi API when gender or callType changes
	useEffect(() => {
		const updateVapiAssistant = async () => {
			const assistantId = getAssistantIdForCallType(callType);
			const newVoiceId = getRandomVoiceId(gender);
			setVoiceId(newVoiceId.name);
			const payload = {
				voice: {
					voiceId: newVoiceId.name,
					provider: newVoiceId.provider,
				},
				recordingEnabled: true,
				server: {
					url: "https://ringringcloser.com/api/api/calls/vapi/webhook",
					timeoutSeconds: 20,
				},
				serverMessages: ["end-of-call-report"],
			};
			try {
				const apiKey =
					process.env.REACT_APP_VAPI_PRIVATE_API_KEY ||
					"b3914ca8-b10b-4a5f-bf67-12f688e332b3";
				await fetch(`https://api.vapi.ai/assistant/${assistantId}`, {
					method: "PATCH",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${apiKey}`,
					},
					body: JSON.stringify(payload),
				});
			} catch (err) {
				// Optionally handle error
				console.error("Vapi API error:", err);
			}
		};
		updateVapiAssistant();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [gender, callType]);

	useEffect(() => {
		fetchDashboardData();
		fetchFeedback();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchDashboardData = async () => {
		try {
			const response = await api.get("api/dashboard/trainee");
			if (response.ok) {
				setDashboardData(response.data);
			} else {
				toast.error("Failed to load dashboard data");
			}
		} catch (error) {
			console.error("Error fetching dashboard data:", error);
			toast.error("An error occurred while loading dashboard");
		} finally {
			setLoading(false);
		}
	};

	const fetchFeedback = async () => {
		setFeedbackLoading(true);
		try {
			const response = await api.get("api/feedback");
			if (response.ok) {
				setFeedback(response.data.items || []);
			} else {
				toast.error("Failed to load your feedback");
			}
		} catch (error) {
			console.error("Error fetching feedback:", error);
			toast.error("An error occurred while loading feedback");
		} finally {
			setFeedbackLoading(false);
		}
	};

	const getAssistantIdForCallType = (type) => {
		switch (type) {
			case "Anytime Script":
				return process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT || "";
			case "Anytime Script - L1":
				return (
					process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT_L1 ||
					"cecbb4ff-770b-47c9-83ef-e999aaad8645"
				);
			case "Anytime Script - L2":
				return (
					process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT_L2 ||
					"e469bc10-2ef6-4935-b16d-ff2be4feacb9"
				);
			case "Anytime Script - L3":
				return (
					process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT_L3 ||
					"29fca220-e33f-482e-b214-ebde1a6f7a00"
				);
			case "Anytime Script - L4":
				return (
					process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT_L4 ||
					"98cb6512-91b1-4ee3-bdf6-ed804ad90e9a"
				);
			case "ISO":
				return process.env.REACT_APP_VAPI_ASSISTANT_ID_ISO || "";
			case "PMI Additionals":
				return process.env.REACT_APP_VAPI_ASSISTANT_ID_PMI_ADDITIONALS || "";
			case "MAINT Maintenance":
				return process.env.REACT_APP_VAPI_ASSISTANT_ID_MAINT_MAINTENANCE || "";
			default:
				return process.env.REACT_APP_VAPI_ASSISTANT_ID || "";
		}
	};

	const handleCallTypeChange = (newCallType) => {
		setCallType(newCallType);
		// Reset issue when switching away from ISO
		if (newCallType !== "ISO") {
			setIssue("");
		}
	};

	// --- Ringtone logic ---
	const ringtoneRef = useRef(null);
	const ringtoneTimerRef = useRef(null);

	// Play ringtone for 3 seconds or until call connects
	const playRingtone = () => {
		if (ringtoneRef.current) {
			ringtoneRef.current.currentTime = 0;
			ringtoneRef.current.play();
			ringtoneTimerRef.current = setTimeout(() => {
				//stopRingtone();
			}, 3000);
		}
	};

	// Stop ringtone and clear timer
	const stopRingtone = () => {
		if (ringtoneRef.current) {
			ringtoneRef.current.pause();
			ringtoneRef.current.currentTime = 0;
		}
		if (ringtoneTimerRef.current) {
			clearTimeout(ringtoneTimerRef.current);
			ringtoneTimerRef.current = null;
		}
	};

	// Stop ringtone when call becomes active
	useEffect(() => {
		if (callState.active) {
			stopRingtone();
		}
	}, [callState.active]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			stopRingtone();
		};
	}, []);

	const startVapiCall = async () => {
		try {
			setCallState((s) => ({ ...s, starting: true }));
			const newFeedbackId = generateGUID();
			setFeedbackId(newFeedbackId);
			const assistantId = getAssistantIdForCallType(callType);
			playRingtone(); // <-- Play ringtone before starting call
			let score = 0;
			let feedback = "";
			await startAssistant(assistantId, {
				feedbackId: newFeedbackId,
				traineeId: user?.id || user?._id,
				traineeName: user?.name,
				traineeEmail: user?.email,
				callType,
				customerMood,
				issue: callType === "ISO" ? issue : undefined,
				score,
				feedback,
			});

			setCallState({
				active: true,
				callId: null,
				starting: false,
				stopping: false,
			});
			toast.success("Call started");
		} catch (err) {
			console.error(err);
			toast.error(err?.message || "Error starting call");
			setCallState((s) => ({ ...s, starting: false }));
			stopRingtone(); // Stop ringtone if call fails
		}
	};

	const stopVapiCall = async () => {
		try {
			setCallState((s) => ({ ...s, stopping: true }));
			await stopAssistant();
			toast.info("Call stopped");
			setCallState({
				active: false,
				callId: null,
				starting: false,
				stopping: false,
			});
			setFieldsDisabled(false); // Enable fields again after call stops
			// Refresh after stopping
			fetchDashboardData();
			fetchFeedback();
			setTimeout(() => {
				window.location.reload();
			}, 10000); // Reload after 10 seconds
		} catch (err) {
			console.error(err);
			toast.error(err?.message || "Error stopping call");
			setCallState((s) => ({ ...s, stopping: false }));
		}
	};

	const callTypes = [
		"Anytime Script",
		"Anytime Script - L1",
		"Anytime Script - L2",
		"Anytime Script - L3",
		"Anytime Script - L4",
		"ISO",
		"PMI Additionals",
		"MAINT Maintenance",
	];
	const moods = [
		"Happy",
		"Skeptical",
		"Anxious",
		"Frustrated",
		"Disappointed",
		"Neutral",
		"Difficult",
	];

	// Handler to randomize all selections
	const handleRandomize = () => {
		// Only randomize gender and mood, not callType
		const randomMood = moods[Math.floor(Math.random() * moods.length)];
		const randomGender = Math.random() < 0.5 ? "M" : "F";
		setCustomerMood(randomMood);
		setGender(randomGender);
		setFieldsDisabled(true);
	};

	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [audioUrl, setAudioUrl] = useState("");
	const [transcriptUrl, setTranscriptUrl] = useState("");
	const [callsummary, setCallsummary] = useState("");
	const [feedbackDownload, setFeedbackDownload] = useState({});
	const [fieldsDisabled, setFieldsDisabled] = useState(false);

	const handlePlay = (row) => {
		if (!row.callRecordFileinfo) return;
		const audioUrl = `${
			process.env.REACT_APP_BASE_API_URL
		}/api/calls/files?path=${encodeURIComponent(row.callRecordFileinfo)}`;
		const transcriptPath =
			row.artifactTranscriptFilePath || row.transcriberJsonFilePath;
		const transcriptUrl = transcriptPath
			? `${
					process.env.REACT_APP_BASE_API_URL
			  }/api/calls/files?path=${encodeURIComponent(transcriptPath)}`
			: "";
		const callsummary = row.callsummary || "";
		const transcriberHref = row.transcriberFilePath
			? `${
					process.env.REACT_APP_BASE_API_URL
			  }/api/calls/files?path=${encodeURIComponent(row.transcriberFilePath)}`
			: null;
		const transcriberTitle = row.transcriberFilePath
			? getFileName(row.transcriberFilePath)
			: "";
		const callRecordHref = audioUrl;
		const callRecordTitle = row.callRecordFileinfo
			? getFileName(row.callRecordFileinfo)
			: "";
		// Build shareable /call link with correct base path for deployment
		const basePath = DEPLOYMENT_CONFIG.getCurrentPath();
		const params = new URLSearchParams({
			audioUrl,
			transcriptUrl,
			callsummary,
			transcriberHref: transcriberHref || "",
			transcriberTitle: transcriberTitle || "",
			callRecordHref,
			callRecordTitle: callRecordTitle || "",
		});
		const shareableLink = `${
			window.location.origin
		}${basePath}/call?${params.toString()}`;
		setAudioUrl(audioUrl);
		setTranscriptUrl(transcriptUrl);
		setCallsummary(callsummary);
		setFeedbackDownload({
			transcriberHref,
			transcriberTitle,
			callRecordHref,
			callRecordTitle,
			shareableLink,
		});
		setSidebarOpen(true);
	};

	if (loading) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "50vh",
					fontSize: 18,
					color: "#6b7280",
				}}
			>
				<LoadingSpinner /> &nbsp;Loading dashboard...
			</div>
		);
	}

	return (
		<div className="tdb tdb-corp">
			<DashboardHeader user={user} />
			<AccountStrip userInfo={dashboardData?.userInfo} />

			<div className="tdb-grid">
				{/* LEFT — Start a Call */}
				<aside className="tdb-side">
					<div className="card">
						<div
							className="card-header"
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
							}}
						>
							<h2 className="card-title">Start a Call</h2>
							<button
								className="btn btn-secondary"
								style={{
									display: "flex",
									alignItems: "center",
									gap: 6,
									fontSize: 14,
									padding: "4px 12px",
								}}
								onClick={handleRandomize}
								title="Randomize selections"
							>
								<span
									role="img"
									aria-label="dice"
								>
									🎲
								</span>{" "}
								Random
							</button>
						</div>

						<CallControls
							callType={callType}
							customerMood={customerMood}
							gender={gender}
							issue={issue}
							callState={callState}
							onChangeCallType={handleCallTypeChange}
							onChangeCustomerMood={setCustomerMood}
							onChangeGender={setGender}
							onChangeIssue={setIssue}
							onStart={startVapiCall}
							onStop={stopVapiCall}
							fieldsDisabled={fieldsDisabled}
						/>

						{callState.active && <CallStatusBanner callState={callState} />}
					</div>
				</aside>

				{/* RIGHT — Review & Scores */}
				<main className="tdb-main">
					<div className="card tdb-fb">
						<div className="card-header">
							<h2 className="card-title">Review &amp; Scores</h2>
							<div className="reviews-search-bar">
								<div className="reviews-search-wrapper">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="reviews-search-icon"
										fill="none"
										viewBox="0 0 24 24"
										strokeWidth={1.8}
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M21 21l-4.35-4.35m1.85-4.65a7 7 0 11-14 0 7 7 0 0114 0z"
										/>
									</svg>
									<input
										type="text"
										className="reviews-search-input"
										placeholder="Search by trainee, email, call type, feedback, or ID"
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
									/>
								</div>
							</div>
						</div>
						<FeedbackTable
							feedback={feedback}
							loading={feedbackLoading}
							baseApiUrl={process.env.REACT_APP_BASE_API_URL}
							getFileName={getFileName}
							onPlay={handlePlay}
						/>
					</div>
				</main>
			</div>

			{/* Hidden ringtone element */}
			<audio
				ref={ringtoneRef}
				src={getRingtoneUrl()}
				preload="auto"
			/>

			{/* Transcript overlay */}
			{sidebarOpen &&
				TranscriptPlayer &&
				require("react-dom").createPortal(
					<TranscriptPlayer
						audioUrl={audioUrl}
						transcriptUrl={transcriptUrl}
						callsummary={callsummary}
						onClose={() => setSidebarOpen(false)}
						transcriberHref={feedbackDownload?.transcriberHref}
						transcriberTitle={feedbackDownload?.transcriberTitle}
						callRecordHref={feedbackDownload?.callRecordHref}
						callRecordTitle={feedbackDownload?.callRecordTitle}
						shareableLink={feedbackDownload?.shareableLink}
					/>,
					document.body,
				)}
		</div>
	);
};

export default TraineeDashboard;
